import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

import { getFilteredProducts } from '../api/products';
import { Product, CATEGORIES, FRAME_TYPES, FRAME_SHAPES, IDEAL_FOR } from '../types';
import ProductCard from '../components/ProductCard';

const parseList = (str: string | null) => str ? str.split(',').map(Number).filter(n => !isNaN(n)) : [];

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price', label: 'Price: Low to High' },
  { value: 'bestSeller', label: 'Best Sellers' },
];

const ProductsPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [selectedCategories, setSelectedCategories] = useState<number[]>(() => parseList(searchParams.get('category_id') || searchParams.get('category')));
  const [selectedFrameTypes, setSelectedFrameTypes] = useState<number[]>(() => parseList(searchParams.get('frame_type_id')));
  const [selectedFrameShapes, setSelectedFrameShapes] = useState<number[]>(() => parseList(searchParams.get('frame_shape_id')));
  const [selectedIdealFor, setSelectedIdealFor] = useState<number[]>(() => parseList(searchParams.get('ideal_for')));
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>(() => parseList(searchParams.get('product_ids')));
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'newest');
  const [filterOpen, setFilterOpen] = useState(false);

  // Sync URL params into local state whenever URL changes (subsequent navigation)
  useEffect(() => {
    setSelectedCategories(parseList(searchParams.get('category_id') || searchParams.get('category')));
    setSelectedFrameTypes(parseList(searchParams.get('frame_type_id')));
    setSelectedFrameShapes(parseList(searchParams.get('frame_shape_id')));
    setSelectedIdealFor(parseList(searchParams.get('ideal_for')));
    setSelectedProductIds(parseList(searchParams.get('product_ids')));
    setSortBy(searchParams.get('sort') || 'newest');
    window.scrollTo(0, 0);
  }, [searchParams]);

  const searchText = searchParams.get('search') || '';

  const buildFilters = useCallback((offset: number) => ({
    categoryIds: selectedCategories.length ? selectedCategories : undefined,
    frameTypeIds: selectedFrameTypes.length ? selectedFrameTypes : undefined,
    frameShapeIds: selectedFrameShapes.length ? selectedFrameShapes : undefined,
    idealFor: selectedIdealFor.length ? selectedIdealFor : undefined,
    productIds: selectedProductIds.length ? selectedProductIds : undefined,
    minPrice: priceRange[0] > 0 ? priceRange[0] : undefined,
    maxPrice: priceRange[1] < 10000 ? priceRange[1] : undefined,
    sortBy,
    searchText: searchText || undefined,
    limit: PAGE_SIZE,
    offset,
  }), [selectedCategories, selectedFrameTypes, selectedFrameShapes, selectedIdealFor, selectedProductIds, priceRange, sortBy, searchText]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    offsetRef.current = 0;
    const { products: results, hasMore: more } = await getFilteredProducts(buildFilters(0));
    setProducts(results);
    setHasMore(more);
    offsetRef.current = results.length;
    setLoading(false);
  }, [buildFilters]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const { products: more, hasMore: stillMore } = await getFilteredProducts(buildFilters(offsetRef.current));
    setProducts((prev) => [...prev, ...more]);
    setHasMore(stillMore);
    offsetRef.current += more.length;
    setLoadingMore(false);
  }, [loadingMore, hasMore, buildFilters]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // IntersectionObserver: trigger fetchMore when sentinel enters viewport
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) fetchMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchMore]);

  const toggle = (list: number[], setList: (v: number[]) => void, id: number) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedFrameTypes([]);
    setSelectedFrameShapes([]);
    setSelectedIdealFor([]);
    setPriceRange([0, 10000]);
  };

  const categoryLabel = selectedProductIds.length > 0 
    ? 'Selected Collection' 
    : (selectedCategories.length === 1
      ? CATEGORIES[selectedCategories[0]]
      : 'All Products');

  return (
    <div className="products-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <span>{categoryLabel}</span>
      </div>

      <div className="products-page__layout">
        {/* Sidebar filter */}
        <aside className={`filter-sidebar ${filterOpen ? 'filter-sidebar--open' : ''}`}>
          <div className="filter-sidebar__header">
            <h3>Filter By</h3>
            <button className="filter-sidebar__clear" onClick={clearFilters}>Clear All</button>
          </div>

          {/* Category */}
          <FilterSection title="Category">
            {Object.entries(CATEGORIES).map(([id, label]) => (
              <CheckItem
                key={id}
                label={label}
                checked={selectedCategories.includes(Number(id))}
                onChange={() => toggle(selectedCategories, setSelectedCategories, Number(id))}
              />
            ))}
          </FilterSection>

          {/* Frame Type */}
          <FilterSection title="Frame Type">
            {Object.entries(FRAME_TYPES).map(([id, label]) => (
              <CheckItem
                key={id}
                label={label}
                checked={selectedFrameTypes.includes(Number(id))}
                onChange={() => toggle(selectedFrameTypes, setSelectedFrameTypes, Number(id))}
              />
            ))}
          </FilterSection>

          {/* Frame Shape */}
          <FilterSection title="Frame Shape">
            {Object.entries(FRAME_SHAPES).map(([id, label]) => (
              <CheckItem
                key={id}
                label={label}
                checked={selectedFrameShapes.includes(Number(id))}
                onChange={() => toggle(selectedFrameShapes, setSelectedFrameShapes, Number(id))}
              />
            ))}
          </FilterSection>

          {/* Ideal For */}
          <FilterSection title="Ideal For">
            {Object.entries(IDEAL_FOR).map(([id, label]) => (
              <CheckItem
                key={id}
                label={label}
                checked={selectedIdealFor.includes(Number(id))}
                onChange={() => toggle(selectedIdealFor, setSelectedIdealFor, Number(id))}
              />
            ))}
          </FilterSection>

          {/* Price Range */}
          <FilterSection title="Price Range">
            <div className="price-range">
              <div className="price-range__labels">
                <span>₹{priceRange[0]}</span>
                <span>₹{priceRange[1]}</span>
              </div>
              <input
                type="range" min={0} max={10000} step={100}
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                className="price-range__slider"
              />
            </div>
          </FilterSection>

          <button className="btn btn--primary filter-apply-btn" onClick={fetchProducts}>
            Apply Filter
          </button>
        </aside>

        {/* Products area */}
        <main className="products-main">
          <div className="products-main__top">
            <div>
              <h1 className="products-main__title">{categoryLabel}</h1>
              <p className="products-main__count">
                Showing {products.length}{hasMore ? '+' : ''} results
              </p>
            </div>
            <div className="products-main__controls">
              <button
                className="filter-toggle-btn"
                onClick={() => setFilterOpen(!filterOpen)}
              >
                ⚙ Filters
              </button>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="products-grid">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton-card" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <p>No products found. Try adjusting your filters.</p>
              <button className="btn btn--primary" onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map((p) => <ProductCard key={p.id} product={p} onWishlistChange={fetchProducts} />)}
              </div>
              {loadingMore && (
                <div className="products-grid">
                  {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton-card" />)}
                </div>
              )}
              <div ref={sentinelRef} style={{ height: 1 }} />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

const FilterSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="filter-section">
      <button className="filter-section__header" onClick={() => setOpen(!open)}>
        <span>{title}</span>
        <span>{open ? '∧' : '∨'}</span>
      </button>
      {open && <div className="filter-section__body">{children}</div>}
    </div>
  );
};

const CheckItem: React.FC<{ label: string; checked: boolean; onChange: () => void }> = ({ label, checked, onChange }) => (
  <label className="check-item">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span>{label}</span>
  </label>
);

export default ProductsPage;
